// vim: set ts=2 sts=2 sw=2 et:

import React, { useMemo, useState } from "react";
import Alert from "react-bootstrap/Alert";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Modal from "react-bootstrap/Modal";
import Table from "react-bootstrap/Table";

import type { Entry, Language } from "../../types/dataTypes";
import type { MeetState } from "../../types/stateTypes";
import { getWeightClassChange } from "../../logic/weightClassChange";
import { displayWeight } from "../../logic/units";
import type { FieldValue, WebPatch, WebPatchRow, WebPatchSection } from "./pullFromWeb";
import { FIELD_LABEL } from "./pullFromWeb";

type Props = {
  show: boolean;
  patch: WebPatch;
  fetchedAt: Date;
  entries: ReadonlyArray<Entry>;
  meet: MeetState;
  language: Language;
  onCancel: () => void;
  onApply: (selectedKeys: ReadonlySet<string>, applyMeet: boolean) => void;
};

const SECTION_TITLE: Record<WebPatchSection, string> = {
  tandas: "Tandas (día, tarima y tanda)",
  pesaje: "Pesaje",
};

function formatValue(row: WebPatchRow, value: FieldValue, language: Language): string {
  if (value === null || value === "") return "—";
  if (typeof value === "boolean") return value ? "sí" : "no";
  if (typeof value === "number") {
    if (row.field === "day" || row.field === "platform") return String(value);
    return value === 0 ? "—" : `${displayWeight(value, language)} kg`;
  }
  return value;
}

const statusStyle: Record<WebPatchRow["status"], React.CSSProperties> = {
  clean: {},
  conflict: { background: "rgba(255, 193, 7, 0.15)" },
  blocked: { opacity: 0.6 },
};

/** Vista previa de "Traer cambios de la web": nada se escribe hasta "Aplicar". */
const WrpfPullFromWebModal = ({ show, patch, fetchedAt, entries, meet, language, onCancel, onApply }: Props) => {
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(patch.rows.filter((r) => r.selected).map((r) => r.key)),
  );
  const [applyMeet, setApplyMeet] = useState(true);

  const entryById = useMemo(() => new Map(entries.map((e) => [e.id, e])), [entries]);
  const sections = (["tandas", "pesaje"] as const).map((section) => ({
    section,
    rows: patch.rows.filter((r) => r.section === section).sort((a, b) => a.entryName.localeCompare(b.entryName, "es")),
  }));
  const selectedCount = patch.rows.filter((r) => r.status !== "blocked" && selected.has(r.key)).length;
  const nothing = patch.rows.length === 0 && patch.meet === null;

  const toggle = (key: string, on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(key);
      else next.delete(key);
      return next;
    });

  const weightClassNote = (row: WebPatchRow): string | null => {
    if (row.field !== "bodyweightKg" || typeof row.incoming !== "number") return null;
    const entry = entryById.get(row.entryId);
    if (!entry) return null;
    const change = getWeightClassChange({ ...entry, bodyweightKg: row.incoming }, meet, language);
    return change ? `categoría ${change.registered} → ${change.weighed}` : null;
  };

  return (
    <Modal show={show} onHide={onCancel} size="xl" backdrop="static" scrollable>
      <Modal.Header closeButton>
        <Modal.Title>Traer cambios de la web</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted small">
          Datos de la web de las {fetchedAt.toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" })}. Solo
          se escriben los campos tildados: nunca intentos, estados de intentos, divisiones ni equipamiento.
        </p>

        {nothing && <Alert variant="success">No hay cambios: esta PC ya tiene lo mismo que la web.</Alert>}

        {patch.newInWeb.length > 0 && (
          <Alert variant="info">
            <strong>{patch.newInWeb.length} atleta(s) de la web no están en este OpenLifter</strong>:{" "}
            {patch.newInWeb.join(", ")}. No se agregan desde acá; cargalos en Inscripción si corresponde.
          </Alert>
        )}

        {patch.meet && (
          <Form.Check
            id="wrpf-pull-meet"
            type="checkbox"
            className="mb-3"
            checked={applyMeet}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setApplyMeet(e.target.checked)}
            label={
              <span>
                <strong>Días y tarimas del torneo:</strong> {patch.meet.local.lengthDays} días (desde{" "}
                {patch.meet.local.date}) → {patch.meet.incoming.lengthDays} días (desde {patch.meet.incoming.date}),
                tarimas por día {patch.meet.incoming.platformsOnDays.join("/")}. Nunca baja de lo que usan los atletas.
              </span>
            }
          />
        )}

        {sections.map(({ section, rows }) =>
          rows.length === 0 ? null : (
            <div key={section} className="mb-3">
              <h6>
                {SECTION_TITLE[section]} · {rows.length}
              </h6>
              <Table size="sm" bordered responsive>
                <thead>
                  <tr>
                    <th style={{ width: "2.5rem" }} />
                    <th>Atleta</th>
                    <th>Campo</th>
                    <th>En esta PC</th>
                    <th>En la web</th>
                    <th>Nota</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.key} style={statusStyle[row.status]}>
                      <td>
                        <Form.Check
                          id={`wrpf-pull-${row.key}`}
                          type="checkbox"
                          aria-label={`Aplicar ${FIELD_LABEL[row.field]} de ${row.entryName}`}
                          disabled={row.status === "blocked"}
                          checked={row.status !== "blocked" && selected.has(row.key)}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => toggle(row.key, e.target.checked)}
                        />
                      </td>
                      <td>{row.entryName}</td>
                      <td>{FIELD_LABEL[row.field]}</td>
                      <td>{formatValue(row, row.local, language)}</td>
                      <td>
                        <strong>{formatValue(row, row.incoming, language)}</strong>
                      </td>
                      <td className="small">
                        {row.status === "conflict" && <strong>Conflicto. </strong>}
                        {row.status === "blocked" && <strong>No se aplica. </strong>}
                        {row.reason}
                        {weightClassNote(row) && <div>{weightClassNote(row)}</div>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ),
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={onCancel}>
          Cancelar
        </Button>
        <Button
          variant="success"
          disabled={selectedCount === 0 && !(patch.meet && applyMeet)}
          onClick={() => onApply(selected, applyMeet)}
        >
          Aplicar {selectedCount} cambio(s)
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default WrpfPullFromWebModal;
